import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { BookOpen, Check, Loader2, AlertCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const token = searchParams.get("token");

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerifying(false);
      setError("Invalid verification link");
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      await axios.post(`${API}/auth/verify-email`, { token });
      setSuccess(true);
      toast.success("Email verified successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center">
        <Card className="card-base max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#4F46E5] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Verifying Your Email...
            </h2>
            <p className="text-slate-500">Please wait while we verify your email address</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <nav className="container-main py-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-[#4F46E5] to-[#14B8A6] rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            BookTest<span className="text-[#4F46E5]">.pro</span>
          </span>
        </Link>
      </nav>

      {/* Content */}
      <div className="container-main py-12 flex items-center justify-center">
        <Card className="card-base max-w-md w-full">
          <CardContent className="p-8 text-center">
            {success ? (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Email Verified!
                </h2>
                <p className="text-slate-600 mb-6">
                  Your email has been verified successfully. You can now access all features.
                </p>
                <Button onClick={() => navigate("/login")} className="w-full btn-primary">
                  Continue to Login
                </Button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Verification Failed
                </h2>
                <p className="text-slate-600 mb-6">{error}</p>
                <div className="space-y-3">
                  <Button onClick={() => navigate("/login")} variant="outline" className="w-full">
                    Go to Login
                  </Button>
                  <p className="text-sm text-slate-500">
                    Need a new verification link?{" "}
                    <Link to="/resend-verification" className="text-[#4F46E5] hover:underline">
                      Resend email
                    </Link>
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
